import * as React from 'react';
// mui
import {
  Box,
  Collapse,
  IconButton,
  ListItemButton,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
// icons
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// dayjs
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
// redux
import { useSelector } from 'react-redux';
import { selectTaskById, selectTasksByParentId } from '../../../store/selectors/taskSelectors';
import { selectRecurringTasksByParentId } from '../../../store/selectors/recuringTaskSelectors';
import { selectEventsByParentId } from '../../../store/selectors/eventSelectors';
// types
import Task from '../../../interfaces/Task.interface';
import Event from '../../../interfaces/Event.interface';
import RecurringTask from '../../../interfaces/RecurringTask.interface';
// components
import TaskDialog from './TaskDialog';
import TaskList from '../TaskList';
import TaskItemContent from './TaskItemContent';
import TaskItemActions from './TaskItemActions';
import EventDialog from '../EventItem/EventDialog';
import RecurringTaskDialog from '../RecurringTaskItem/RecurringTaskDialog';
import AddMiniTaskDialog from './AddMiniTaskDialog';

interface TaskItemProps {
  padding: number;
  parentTaskId: number | null;
  taskId: number;
  showProject?: boolean;
}

export default function TaskItem(props: TaskItemProps) {
  // hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const showProject = props.showProject || false;
  
  // props
  const taskId: number = props.taskId;
  const inheritedPadding: number = isMobile ? props.padding / 2 : props.padding;
  const parentTaskId: number | null = props.parentTaskId;

  // redux
  const localTask: Task = useSelector(selectTaskById(taskId)) as Task;
  const localSubtasks: Task[] = useSelector(selectTasksByParentId(taskId));
  const localEvents: Event[] = useSelector(selectEventsByParentId(taskId));
  const localRecurringTasks: RecurringTask[] = useSelector(selectRecurringTasksByParentId(taskId));

  const hasSubtasks =
    localSubtasks.length
    + localEvents.length
    + localRecurringTasks.length
    + localTask.miniTasks.length !== 0;

  // local state
  const [ showSubtasks, setShowSubtasks ] = React.useState(false);
  const [ itemIsHovered, setItemIsHovered ] = React.useState<boolean>(false);

  // edit task dialog
  const [ editTaskDialogStatus, setEditTaskDialogStatus ] = React.useState(false);
  // add task dialog
  const [ addTaskDialogStatus, setAddTaskDialogStatus ] = React.useState(false);
  // add event dialog
  const [ addEventDialogStatus, setAddEventDialogStatus ] = React.useState(false);
  // add recurring task dialog
  const [ addRecurringTaskDialogStatus, setAddRecurringTaskDialogStatus ] = React.useState(false);
  // add recurring task dialog
  const [ addMiniTaskDialogStatus, setAddMiniTaskDialogStatus ] = React.useState(false);

  const handleEditDialogSave = () => {
    setEditTaskDialogStatus(false);
  }

  const handleLocalDropdownClick = (event: any) => {
    event.stopPropagation(); // prevent dialog from opening when button clicked
    setShowSubtasks(!showSubtasks);
  }

  React.useEffect(() => {
    if (!hasSubtasks) {
      setShowSubtasks(false);
    }
  }, [hasSubtasks])

  return (
    <Box>
      <ListItemButton
        sx={{ pl: inheritedPadding+1, pr: isMobile ? 1 : '' }}
        onMouseEnter={() => setItemIsHovered(true)}
        onMouseLeave={() => setItemIsHovered(false)}
        onClick={() => { setEditTaskDialogStatus(true); }}
      >
        {/* dropdown button with dynamic icon */}
        <ListItemIcon
          sx={{ minWidth: isMobile ? 36 : 46, color: 'lightgrey' }}
        >
          <IconButton
            disabled={!hasSubtasks}
            sx={{ p: isMobile ? 1 : '' }}
            onClick={handleLocalDropdownClick}
          >
            { showSubtasks ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon/> }
          </IconButton>
        </ListItemIcon>

        {/* task information */}
        <TaskItemContent
          taskId={localTask.id}
          showProject={showProject}
        />

        <TaskItemActions
          taskId={localTask.id}
          parentTaskId={parentTaskId}
          isHovered={itemIsHovered}
          onOpenAddTaskDialog={() => { setAddTaskDialogStatus(true); }}
          onOpenAddEventDialog={() => { setAddEventDialogStatus(true); }}
          onOpenAddRecurringTaskDialog={() => { setAddRecurringTaskDialogStatus(true); }}
          onOpenAddMiniTaskDialog={() => { setAddMiniTaskDialogStatus(true); }}
        />
      </ListItemButton>
      <Divider sx={{ ml: (isMobile ? inheritedPadding+1 : inheritedPadding), mr: isMobile ? '8px' : 'auto' }} />

      {/* subtasks list */}
      <Collapse in={showSubtasks} timeout="auto" unmountOnExit>
        <TaskList
          parentTaskId={localTask.id}
          padding={inheritedPadding}
        />
      </Collapse>

      {/* dialog for editing */}
      { editTaskDialogStatus &&
        <TaskDialog
          parentTaskId={parentTaskId}
          onSave={handleEditDialogSave}
          onCancel={() => { setEditTaskDialogStatus(false); }}
          taskId={localTask.id}
          addMode={false}
        />
      }

      {/* dialog for adding subtask */}
      { addTaskDialogStatus &&
        <TaskDialog
          onSave={() => { setShowSubtasks(true); setAddTaskDialogStatus(false); }}
          onCancel={() => { setAddTaskDialogStatus(false); }}
          parentTaskId={localTask.id}
          taskId={null}
          addMode={true}
        /> }

      {/* dialog for adding event */}
      { addEventDialogStatus &&
        <EventDialog
          onSave={() => { setShowSubtasks(true); setAddEventDialogStatus(false); }}
          onCancel={() => { setAddEventDialogStatus(false); }}
          parentTaskId={localTask.id}
          eventId={null}
          addMode={true}
        /> }

      {/* dialog for adding recurring subtask */}
      { addRecurringTaskDialogStatus &&
        <RecurringTaskDialog
          onSave={() => { setShowSubtasks(true); setAddRecurringTaskDialogStatus(false); }}
          onCancel={() => { setAddRecurringTaskDialogStatus(false); }}
          parentTaskId={localTask.id}
          recurringTaskId={null}
          addMode={true}
        /> }

      {/* dialog for adding mini task */}
      { addMiniTaskDialogStatus && 
        <AddMiniTaskDialog 
          onSave={() => { setShowSubtasks(true); setAddMiniTaskDialogStatus(false); }}
          onCancel={() => { setAddMiniTaskDialogStatus(false); }}
          parentTaskId={localTask.id}
        /> }
    </Box>
  );
}
